/**
 * One live session, for Control (read/write) and spectators (read-only).
 * Lobby → room board → table drill-down → debrief, with a rail of alerts,
 * the room feed and debrief notes.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { EVENTS, type Role } from '@nightshift/engine/client';
import { linkProps } from '../app/router.ts';
import { api } from '../net/api.ts';
import { useNow, useSocket } from '../net/hooks.ts';
import type { LiveSocket } from '../net/socket.ts';
import { Countdown, Fold, Moon, PhaseGuide, phaseName, ROLE_TITLE, Toasts, useToasts } from '../ui/bits.tsx';
import { DebriefPanel } from './Debrief.tsx';
import { TableDetailView, TableTileView } from './TableView.tsx';
import type { FacultyWire, FeedItem } from './wire.ts';

type Links = { spectatorToken: string; projectorToken: string; spoilers: boolean };

export function rememberLinks(id: string, links: Links): void {
  try {
    sessionStorage.setItem(`ns_links_${id}`, JSON.stringify(links));
  } catch {
    /* ignore */
  }
}
function recallLinks(id: string): Links | null {
  try {
    const raw = sessionStorage.getItem(`ns_links_${id}`);
    return raw ? (JSON.parse(raw) as Links) : null;
  } catch {
    return null;
  }
}

export interface Ctl {
  send: (cmd: Record<string, unknown>) => Promise<boolean>;
  readonly: boolean;
  sessionId: string;
}

export function SessionScreen({ url, sessionId, readonly, check }: { url: string; sessionId: string; readonly: boolean; check?: () => Promise<boolean> }) {
  const { sock, view, status } = useSocket<FacultyWire>(url, check);
  const [selected, setSelected] = useState<number | null>(null);
  const [toasts, toast] = useToasts();
  const now = useNow(250) + (sock?.offset ?? 0);

  // (Re)subscribe to the drilled-down table whenever the socket (re)opens.
  useEffect(() => {
    if (sock && status === 'open') sock.sub(selected);
  }, [sock, status, selected]);

  const ctl: Ctl = useMemo(
    () => ({
      readonly,
      sessionId,
      send: async (cmd) => {
        if (!sock || readonly) return false;
        const r = await sock.send(cmd);
        if (!r.ok) toast(r.reason ?? 'Control could not do that.', true);
        return r.ok;
      },
    }),
    [sock, readonly, sessionId, toast],
  );

  if (status === 'denied') return <main className="fac-loading">
    <p role="alert">{sock?.reason ?? 'This link has expired. Ask Control for a new one.'}</p>
    {!readonly && <p><a {...linkProps('/control')}>Back to sessions</a> · <a {...linkProps(`/control/s/${sessionId}/replay`)}>Saved timeline</a></p>}
  </main>;
  if (!view) return <main className="fac-loading muted">{status === 'open' ? 'Loading the room…' : 'Connecting to the room…'}</main>;

  return (
    <div className={`fac-page session ${readonly ? 'readonly' : ''}`}>
      <ClockBar v={view} now={now} ctl={ctl} />
      {status !== 'open' && <div className="connbar">Reconnecting to the room…</div>}
      <div className="session-body">
        <SessionMain view={view} ctl={ctl} sock={sock} selected={selected} setSelected={setSelected} />
        <Rail v={view} ctl={ctl} selected={selected} onPick={setSelected} />
      </div>
      <Toasts list={toasts} />
    </div>
  );
}

/** Preserve the selected room view independently of connection controls. */
function SessionMain({ view, ctl, sock, selected, setSelected }: { view: FacultyWire; ctl: Ctl; sock: LiveSocket<FacultyWire> | null; selected: number | null; setSelected: (value: number | null) => void }): ReactElement {
  const phase = view.clock.phase;
  const debrief = phase === 'DEBRIEF' || phase === 'ENDED';
  const readonly = ctl.readonly;
  return (
    <main className="session-main">
      <PhaseGuide phase={phase} handoverTurn={view.clock.handoverTurn} />
      {phase === 'SETUP' ? (
        <Lobby v={view} ctl={ctl} sock={sock} />
      ) : debrief && selected === null ? (
        <>
          {view.debrief && <DebriefPanel d={view.debrief} tables={view.tables} sessionId={view.sessionId} readonly={readonly} layers={view.session.layers} endedAt={view.clock.handoverTurn ? null : view.clock.hour} />}
          <Fold title="Tables at the end of the night" hint="boards and tonight's script" testId="end-tables">
            <RoomGrid v={view} onPick={setSelected} />
          </Fold>
        </>
      ) : selected !== null && view.detail && view.detail.no === selected ? (
        <TableDetailView d={view.detail} tile={view.tables.find((t) => t.no === selected)!} spoilers={view.spoilers} ctl={ctl} onBack={() => setSelected(null)} feed={view.feed.filter((f) => f.table === selected)} />
      ) : selected !== null ? (
        <p className="muted">Opening Table {selected}…</p>
      ) : (
        <RoomGrid v={view} onPick={setSelected} />
      )}
    </main>
  );
}

// ───────────────────────────── clock bar & controls ─────────────────────────────

/** Show the timer only while the current phase is timed. */
function SessionClock({ v, now }: { v: FacultyWire; now: number }): ReactElement {
  const c = v.clock;
  const timed = c.phase === 'ACT' || c.phase === 'HANDOVER';
  return <div className="cb-clock">
    <span className="cb-hour display">{c.hour}</span>
    <span className="cb-phase">
      <strong>{phaseName(c.phase)}</strong>
      <span className="muted">{c.turn > 0 ? `Turn ${c.turn} of ${c.lastTurn}` : 'Not started'}</span>
    </span>
    {timed && <Countdown endsAt={c.phaseEndsAt} remainingMs={c.remainingMs} paused={c.paused} now={now} total={c.phaseMs ?? undefined} />}
    {timed && c.hold && <span className="tag">held</span>}
    {c.phase === 'LOG' && <span className="tag">Waiting for Control</span>}
  </div>;
}

/** Separate timed planning controls from the untimed review release. */
export function ClockControls({ v, ctl }: { v: FacultyWire; ctl: Ctl }): ReactElement {
  const c = v.clock;
  const timed = c.phase === 'ACT' || c.phase === 'HANDOVER';
  return <div className="cb-controls">
    {timed && <>
      {c.paused
        ? <button className="btn btn-primary btn-small" onClick={() => ctl.send({ type: 'RESUME' })} data-testid="ctl-resume">Resume</button>
        : <button className="btn btn-small" onClick={() => ctl.send({ type: 'PAUSE' })} data-testid="ctl-pause">Pause</button>}
      <button className="btn btn-small" onClick={() => ctl.send({ type: 'EXTEND', ms: 30000 })} title="Add 30 seconds to the current timer">+30 s</button>
      <button className="btn btn-small" aria-pressed={c.hold} onClick={() => ctl.send({ type: 'HOLD', on: !c.hold })} title="Hold at the deadline until Control advances">
        {c.hold ? 'Release hold' : 'Hold at deadline'}
      </button>
    </>}
    <ControlAdvance v={v} ctl={ctl} />
    <ControlMore v={v} ctl={ctl} />
  </div>;
}

/** Expose readiness and disconnected seats before Control commits. */
function ControlAdvance({ v, ctl }: { v: FacultyWire; ctl: Ctl }): ReactElement {
  const c = v.clock;
  const seats = v.tables.flatMap(t => t.seats.map(s => ({ ...s, table: t.no })));
  const unready = seats.filter(s => !s.ready);
  const offline = seats.filter(s => !s.bot && !v.connected.includes(`${s.table}:${s.role}`));
  const action = c.phase === 'ACT' ? 'End turn' : c.phase === 'LOG' ? 'Start next turn' : 'Open debrief';
  return <div>
    {c.phase === 'ACT' && <>
      <p className="small" role="status" data-testid="commit-readiness">{unready.length} players not ready · {offline.length} phone{offline.length === 1 ? '' : 's'} offline. Every saved plan will go ahead.</p>
      {(unready.length > 0 || offline.length > 0) && <details>
        <summary>Check players before ending the turn</summary>
        <ul>{seats.filter(s => !s.ready || offline.includes(s)).map(s => <li key={`${s.table}:${s.role}`}>
          Table {s.table} · {ROLE_TITLE[s.role]}: {s.ready ? 'Ready' : 'Not ready'}, {s.draftActions} planned{offline.includes(s) ? ', offline' : ''}
        </li>)}</ul>
      </details>}
    </>}
    <button className="btn btn-primary btn-small" onClick={() => ctl.send({ type: 'ADVANCE_PHASE', from: { turn: c.turn, phase: c.phase } })} data-testid="ctl-skip" title={action}>{action}</button>
  </div>;
}

/** Keep session messages and event intervention available to Control. */
function ControlMore({ v, ctl }: { v: FacultyWire; ctl: Ctl }): ReactElement {
  const [broadcast, setBroadcast] = useState('');
  const [inject, setInject] = useState('');
  const injectable = EVENTS.filter(event => !event.layer || v.session.layers[event.layer]);
  return <details className="cb-more">
    <summary className="btn btn-small btn-quiet">More</summary>
    <div className="cb-menu">
      <form className="row" onSubmit={async event => {
        event.preventDefault();
        if (await ctl.send({ type: 'BROADCAST', text: broadcast.trim() || null })) setBroadcast('');
      }}>
        <input className="input" placeholder="Message to every phone" maxLength={140} value={broadcast} onChange={event => setBroadcast(event.target.value)} />
        <button className="btn btn-small">{broadcast.trim() ? 'Send' : 'Clear'}</button>
      </form>
      <div className="row">
        <select className="input" value={inject} onChange={event => setInject(event.target.value)} aria-label="Event to add">
          <option value="">Add an event…</option>
          {injectable.map(event => <option key={event.key} value={event.key}>{event.key}: {event.title}</option>)}
        </select>
        <button className="btn btn-small" disabled={!inject} onClick={() => ctl.send({ type: 'INJECT', event: inject, when: 'next' })}>Next turn</button>
      </div>
      <button className="btn btn-small btn-danger" onClick={() => {
        if (window.confirm('End the shift now and go to the debrief?')) void ctl.send({ type: 'END_SHIFT' });
      }}>End the shift now</button>
    </div>
  </details>;
}

function ClockBar({ v, now, ctl }: { v: FacultyWire; now: number; ctl: Ctl }): ReactElement {
  const running = ['ACT', 'LOG', 'HANDOVER'].includes(v.clock.phase);
  return <header className="clockbar">
    <div className="cb-left">
      {!ctl.readonly ? <a className="cb-back" {...linkProps('/control')} aria-label="All sessions"><Moon size={22} /></a> : <Moon size={22} />}
      <div className="cb-title">
        <strong>{v.session.name}</strong>
        <span className="muted">
          {v.session.presetLabel}, {v.session.tables} table{v.session.tables > 1 ? 's' : ''}
          {v.code ? <>, join code <b className="mono cb-code">{v.code}</b></> : null}
        </span>
      </div>
    </div>
    <SessionClock v={v} now={now} />
    {!ctl.readonly && running && <ClockControls v={v} ctl={ctl} />}
    {!ctl.readonly && !running && v.clock.phase !== 'SETUP' && <div className="cb-controls">
      <a className="btn btn-small" {...linkProps(`/control/s/${v.sessionId}/replay`)}>Replay and highlights</a>
      <a className="btn btn-small btn-quiet" href={`/api/sessions/${v.sessionId}/export.json`} download>Export log</a>
      <a className="btn btn-small btn-quiet" href={`/api/sessions/${v.sessionId}/export.csv`} download>CSV</a>
    </div>}
    {v.broadcast && <p className="cb-broadcast">On every phone: “{v.broadcast}”</p>}
  </header>;
}

// ───────────────────────────── lobby ─────────────────────────────

const SEAT_ORDER: Role[] = ['EM', 'SURG', 'PHYS', 'ICU', 'PSYCH'];
const CORE_SEATS: Role[] = ['EM', 'SURG', 'PHYS', 'ICU'];

function Lobby({ v, ctl, sock }: { v: FacultyWire; ctl: Ctl; sock: LiveSocket<FacultyWire> | null }) {
  const [links, setLinks] = useState<Links | null>(() => recallLinks(v.sessionId));
  const [err, setErr] = useState<string | null>(null);
  const connected = new Set(v.connected);
  const missing = new Map(v.tables.map((t) => [t.no, CORE_SEATS.filter((r) => !t.seats.some((s) => s.role === r))]));
  const ready = [...missing.values()].every((m) => m.length === 0);
  const people = v.tables.reduce((n, t) => n + t.seats.filter((s) => !s.bot).length, 0);
  const why = v.tables
    .filter((t) => missing.get(t.no)!.length > 0)
    .map((t) => `Table ${t.no} needs ${missing.get(t.no)!.map((r) => ROLE_TITLE[r]).join(', ')}`)
    .join('. ');
  const origin = location.origin;
  const mint = async (spoilers: boolean) => {
    try {
      const l = await api<Links>(`/api/sessions/${v.sessionId}/links`, { body: { spoilers } });
      rememberLinks(v.sessionId, l);
      setLinks(l);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <div className="lobby">
      <section className="lobby-intro">
        <h1 className="display">Seat the room</h1>
        <p className="muted">
          Players open <b>{origin.replace(/^https?:\/\//, '')}</b> and enter <b className="mono">{v.code}</b>, or scan their table’s code. Each table needs Emergency, Surgeon, Physician and Intensivist; a Psychiatrist is optional. Bots can fill gaps.
        </p>
        {!ctl.readonly && (
          <div className="row wrap">
            <button
              className="btn"
              onClick={async () => {
                if (!sock || sock.commandError) { setErr(sock?.commandError ?? 'Reconnecting — try again in a moment.'); return; }
                try {
                  await api(`/api/sessions/${v.sessionId}/fill-bots`, { body: { roles: ['EM', 'SURG', 'PHYS', 'ICU'] } });
                } catch (e) {
                  setErr((e as Error).message);
                }
              }}
              data-testid="fill-bots"
            >
              Fill empty seats with bots
            </button>
            <button className="btn btn-primary" disabled={!ready} onClick={() => ctl.send({ type: 'START_SHIFT' })} data-testid="start-shift" title={ready ? 'Deal the objectives and open the doors' : 'Every table needs the four core seats'}>
              Start the shift
            </button>
          </div>
        )}
        <p className={`lobby-status ${ready ? 'ok' : ''}`} data-testid="lobby-status" role="status">
          {people} {people === 1 ? 'person' : 'people'} seated. {ready ? 'Every table has its four core seats.' : `${why}.`}
        </p>
        {err && <p className="form-error">{err}</p>}
      </section>
      <div className="lobby-tables">
        {v.tables.map((t) => (
          <article key={t.no} className="lobby-table">
            <header>
              <h2 className="display">Table {t.no}</h2>
              <p className={`lobby-need ${missing.get(t.no)!.length ? '' : 'ok'}`} data-testid={`need-${t.no}`}>
                {missing.get(t.no)!.length ? `Needs ${missing.get(t.no)!.map((r) => ROLE_TITLE[r]).join(', ')}` : 'Ready'}
              </p>
              <img className="qr" src={`/api/sessions/${v.sessionId}/qr/${t.no}`} alt={`QR code to join Table ${t.no}`} width={112} height={112} />
            </header>
            <ul className="lobby-seats">
              {SEAT_ORDER.map((r) => {
                const s = t.seats.find((x) => x.role === r);
                const on = connected.has(`${t.no}:${r}`);
                return (
                  <li key={r} className={`spec-${r} ${s ? 'filled' : ''}`}>
                    <span className="spec-dot" />
                    <span className="ls-role">{ROLE_TITLE[r]}</span>
                    <span className="ls-who">{s ? (s.bot ? 'Bot' : (s.name ?? 'Seated')) : r === 'PSYCH' ? <span className="muted">optional</span> : <span className="muted">empty</span>}</span>
                    {s && !s.bot && <span className={`conn ${on ? 'on' : ''}`} title={on ? 'phone connected' : 'phone not connected'} />}
                    {s && !ctl.readonly && (
                      <button
                        className="btn btn-quiet btn-small"
                        onClick={async () => {
                          if (!sock || sock.commandError) { setErr(sock?.commandError ?? 'Reconnecting — try again in a moment.'); return; }
                          try {
                            await api(`/api/sessions/${v.sessionId}/release`, { body: { table: t.no, role: r, bot: false } });
                          } catch (e) {
                            setErr((e as Error).message);
                          }
                        }}
                      >
                        Release
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </article>
        ))}
      </div>
      {!ctl.readonly && (
        <Fold className="lobby-links" title="Projector and spectator links" hint={links ? 'ready' : 'not made yet'} testId="room-links">
          {links ? (
            <dl>
              <dt>Projector</dt>
              <dd>
                <CopyLink url={`${origin}/projector/${links.projectorToken}`} />
              </dd>
              <dt>Spectator {links.spoilers ? '(sees hidden cards)' : '(no spoilers)'}</dt>
              <dd>
                <CopyLink url={`${origin}/watch/${links.spectatorToken}`} />
              </dd>
            </dl>
          ) : (
            <p className="muted">Make links for the projector and for observers. Making new links stops the old ones.</p>
          )}
          <div className="row wrap">
            <button className="btn btn-small" onClick={() => mint(false)}>
              {links ? 'Make new links' : 'Make links'}
            </button>
            <button className="btn btn-small btn-quiet" onClick={() => mint(true)}>
              New links, spectator sees hidden cards
            </button>
          </div>
        </Fold>
      )}
    </div>
  );
}

function CopyLink({ url }: { url: string }) {
  const [done, setDone] = useState(false);
  return (
    <span className="copylink">
      <a href={url} target="_blank" rel="noreferrer" className="mono">
        {url}
      </a>
      <button
        className="btn btn-quiet btn-small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
          } catch {
            /* clipboard blocked: the link is still selectable */
          }
        }}
      >
        {done ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

// ───────────────────────────── room grid ─────────────────────────────

function RoomGrid({ v, onPick }: { v: FacultyWire; onPick: (n: number) => void }) {
  const ev = v.clock.phase === 'DEBRIEF' || v.clock.phase === 'ENDED' ? [] : v.events;
  return (
    <div className="room">
      {ev.length > 0 && (
        <section className="room-events" aria-label="This turn's events">
          {ev.map((e) => (
            <article key={e.key} className="room-event">
              <span className="muted">{e.key}</span>
              <strong>{e.title}</strong>
              <span>{e.rule}</span>
            </article>
          ))}
        </section>
      )}
      <div className="room-grid">
        {v.tables.map((t) => (
          <TableTileView key={t.no} t={t} connected={v.connected} onOpen={() => onPick(t.no)} />
        ))}
      </div>
      {v.spoilers && v.script.length > 0 && <ScriptStrip v={v} />}
    </div>
  );
}

/** The night's script: what arrives when, which events, where we are. */
function ScriptStrip({ v }: { v: FacultyWire }) {
  return (
    <section className="script" aria-label="Tonight's script">
      <h3 className="sec-title">Tonight’s script</h3>
      <ol className="script-row">
        {v.script.map((r) => (
          <li key={r.turn} className={`${r.turn === v.clock.turn ? 'now' : r.turn < v.clock.turn ? 'past' : ''}`}>
            <span className="sc-hour mono">{r.handover ? '0700' : `${String(r.hour % 24).padStart(2, '0')}00`}</span>
            <span className="sc-arr">{r.arrivals} in</span>
            {r.events.map((e) => (
              <span key={e.key} className="sc-ev" title={e.title}>
                {e.key}
              </span>
            ))}
          </li>
        ))}
      </ol>
    </section>
  );
}

// ───────────────────────────── rail ─────────────────────────────

function Rail({ v, ctl, selected, onPick }: { v: FacultyWire; ctl: Ctl; selected: number | null; onPick: (n: number | null) => void }) {
  const [important, setImportant] = useState(true);
  const [note, setNote] = useState('');
  const [star, setStar] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const feed = v.feed.filter((f) => (selected === null || f.table === selected || f.table === null) && (!important || f.severity >= 2)).slice(-60).reverse();
  return (
    <aside className="rail-side">
      {v.alerts.length > 0 && (
        <section className="alerts" aria-label="Needs attention">
          <h3 className="sec-title">Needs attention</h3>
          <ul>
            {v.alerts.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
            {v.tables.flatMap((t) => t.alerts.map((a) => ({ t: t.no, a }))).slice(0, 12).map(({ t, a }) => (
              <li key={`${t}-${a}`}>
                <button className="linkish" onClick={() => onPick(t)}>
                  Table {t}
                </button>
                : {a}
              </li>
            ))}
          </ul>
        </section>
      )}
      {!ctl.readonly && v.clock.phase !== 'SETUP' && (
        <form
          className="note-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!note.trim()) return;
            try {
              await api(`/api/sessions/${v.sessionId}/notes`, { body: { table: selected, seq: null, text: note.trim(), starred: star } });
              setNote('');
              setStar(false);
              setMsg('Saved for the debrief.');
              window.setTimeout(() => setMsg(null), 1800);
            } catch (x) {
              setMsg((x as Error).message);
            }
          }}
        >
          <label className="sec-title" htmlFor="note">
            Note for the debrief{selected ? `, Table ${selected}` : ''}
          </label>
          <textarea id="note" className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you saw, to bring up later" />
          <div className="row">
            <label className="row small">
              <input type="checkbox" checked={star} onChange={(e) => setStar(e.target.checked)} /> Show on the projector at the debrief
            </label>
            <button className="btn btn-small" disabled={!note.trim()}>
              Save
            </button>
          </div>
          {msg && <p className="muted small">{msg}</p>}
        </form>
      )}
      <section className="feed" aria-label="What happened">
        <div className="row feed-head">
          <h3 className="sec-title">{selected ? `Table ${selected}` : 'The room'}</h3>
          <label className="row small">
            <input type="checkbox" checked={important} onChange={(e) => setImportant(e.target.checked)} /> Only what matters
          </label>
        </div>
        <ol className="feed-list">
          {feed.map((f) => (
            <FeedRow key={`${f.seq}-${f.type}-${f.text}`} f={f} />
          ))}
          {feed.length === 0 && <li className="muted">Nothing yet.</li>}
        </ol>
      </section>
    </aside>
  );
}

export function FeedRow({ f, onClick }: { f: FeedItem; onClick?: () => void }) {
  const body = (
    <>
      <span className="fr-when mono">
        {f.table ? `T${f.table} ` : ''}
        {f.turn > 0 ? `${f.turn}` : ''}
      </span>
      <span className="fr-text">
        {f.text}
        {f.tag && <span className="fr-tag"> {f.tag}</span>}
      </span>
    </>
  );
  return (
    <li className={`fr sev-${f.severity} t-${f.type}`}>
      {onClick ? (
        <button className="fr-btn" onClick={onClick}>
          {body}
        </button>
      ) : (
        body
      )}
    </li>
  );
}
